import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  /** Number of awards being revoked (>1 = bulk). */
  count?: number;
  /** Recipient name for the single-award case. */
  recipientName?: string;
  onConfirm: (reason: string, notify: boolean) => void;
  onCancel: () => void;
  pending?: boolean;
}

/**
 * Revoke confirmation with an optional reason and an opt-in to email the
 * recipient. Used for single and bulk revocation on the Awards page.
 */
export function RevokeAwardModal({ open, count, recipientName, onConfirm, onCancel, pending }: Props) {
  const { t } = useTranslation('dashboard');
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(false);
  if (!open) return null;

  const bulk = (count ?? 1) > 1;
  const target = bulk
    ? t('awards.revokeBulkTarget', '{{count}} credentials', { count })
    : (recipientName || t('awards.thisAward', 'this credential'));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-bold text-gray-900">{t('awards.revokeTitle', 'Revoke credential')}</h2>
        <p className="mt-2 text-sm text-gray-600">
          {t('awards.revokeConfirm', 'Revoke {{target}}? It will show as revoked on the public verification page and can no longer be shared. The record is kept for audit; this does not refund the credit.', { target })}
        </p>

        <label className="mt-4 block text-xs font-medium text-gray-700">{t('awards.revokeReasonLabel', 'Reason (optional)')}</label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder={t('awards.revokeReasonPlaceholder', 'e.g. Issued to the wrong recipient')}
          className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
        />

        <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} className="mt-0.5" />
          <span>{t('awards.revokeNotify', 'Email the recipient that their credential was revoked')}</span>
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50">
            {t('common:buttons.cancel', 'Cancel')}
          </button>
          <button
            onClick={() => onConfirm(reason.trim(), notify)}
            disabled={pending}
            className="rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {pending ? t('awards.revoking', 'Revoking…') : t('awards.revokeConfirmBtn', 'Revoke')}
          </button>
        </div>
      </div>
    </div>
  );
}
